import type { BrandContact } from "./generated/brand_contact";
import { contactWhatsappLink } from "../services/cms/contactLinks";

/**
 * SOLS Foundation's registered-entity facts and public contact details in the
 * forms the site shows them, composed from a brand contact. React code passes
 * the contact of the page being shown (`useBrandContact()` in
 * lib/LiveContext.tsx), which is the CMS record read live, or the committed
 * one (constants/generated/brand_contact.ts) on a page without CMS props.
 *
 * Translated strings carry placeholders ({{phone}}, {{email}}, {{address}},
 * {{registrationNumber}}, {{registrationNumberOld}}) composed the same way, in
 * lib/liveValues.ts.
 */
export function contactFacts(contact: BrandContact) {
  const [line1, line2, line3] = contact.streetLines;
  const streetAddress = `${line1}, ${line2}, ${line3}`;
  const locality = `${contact.postalCode} ${contact.addressLocality}, Malaysia`;
  const whatsappUrl = contactWhatsappLink(contact, null);
  const googleMapsUrl = contact.googleMapsUrl || undefined;
  // The Waze button shows only when the record holds a link and switches it on.
  const wazeUrl =
    contact.showWazeButton && contact.wazeUrl ? contact.wazeUrl : undefined;

  return {
    legalName: contact.registeredName,
    /**
     * SSM registration numbers, both current: the 12-digit number SSM
     * re-issued and the original alphanumeric one, which still identifies the
     * same company.
     */
    registrationNumberCurrent: contact.registrationNumber,
    registrationNumberFormer: contact.registrationNumberOld,
    registrationDisplay: `${contact.registrationNumber} (${contact.registrationNumberOld})`,
    /** Structured address for the JSON-LD. */
    orgAddress: {
      "@type": "PostalAddress",
      streetAddress,
      addressLocality: contact.addressLocality,
      postalCode: contact.postalCode,
      addressCountry: "MY",
    } as const,
    /** Four-line display form, each line but the last ending in a comma. */
    addressLines: [`${line1},`, `${line2},`, `${line3},`, locality] as const,
    /** Single-line prose form, for running text and llms.txt. */
    addressOneLine: `${streetAddress}, ${locality}`,
    /** Dialable form, for `tel:` links and the schema `telephone` field. */
    phone: contact.phoneDial,
    /** Spaced display form of the same number. */
    phoneDisplay: contact.generalPhone,
    email: contact.generalEmail,
    /** The link to chat on WhatsApp, with no message. */
    whatsappUrl,
    /** Digits only, for WhatsApp links a page builds with its own message. */
    whatsappDigits: whatsappUrl.replace(/\D/g, ""),
    googleMapsUrl,
    wazeUrl,
    /** Exactly one navigation button shows, so it takes the whole row. */
    navigationLone: Boolean(googleMapsUrl) !== Boolean(wazeUrl),
  };
}

export type ContactFacts = ReturnType<typeof contactFacts>;
